// frontend/src/components/FiscalDataTable.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useForm, Controller } from 'react-hook-form';
import * as XLSX from 'xlsx';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TableSortLabel,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Typography,
    Checkbox,
    ListItemText,
    Autocomplete,
    Chip,
    Popover,
    Grid,
    Link,
    TablePagination, // Импортируем компонент пагинации
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FiscalDataRow from './FiscalDataRow';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
const API_BASE_URL = ``;
const headCells = [
    { id: 'actions', label: 'Действия', sortable: false, filterable: false },
    { id: 'id', label: 'ID', sortable: true, filterable: false },
    { id: 'vip', label: 'Клиент', sortable: true, filterable: true },
    { id: 'client_name', label: 'ЮЛ', sortable: true, filterable: true },
    { id: 'inn', label: 'ИНН', sortable: true, filterable: true },
    { id: 'city', label: 'Город', sortable: true, filterable: true },
    { id: 'object_name', label: 'Объект', sortable: true, filterable: true },
    { id: 'address', label: 'Адрес', sortable: true, filterable: true },
    { id: 'fr', label: 'ФР', sortable: true, filterable: true },
    { id: 'rnm', label: 'РНМ', sortable: true, filterable: true },
    { id: 'zn', label: 'ЗН', sortable: true, filterable: true },
    { id: 'ofd', label: 'ОФД', sortable: true, filterable: true },
    { id: 'fn_end_date', label: 'Срок окончания ФН', sortable: true, filterable: true },
    { id: 'ofd_end_date', label: 'Срок окончания ОФД', sortable: true, filterable: true },
    { id: 'dreamkas_key_date', label: 'Дримкас ключ', sortable: true, filterable: true },
    { id: 'dreamkas_accounting_expiration', label: 'Дримкас учет', sortable: true, filterable: true },
    { id: 'dreamkas_start_expiration', label: 'Дримкас старт', sortable: true, filterable: true },
    { id: 'utm_certificate_expiration', label: 'Сертификат УТМ', sortable: true, filterable: true },
    { id: 'last_check_date_ofd', label: 'Дата последнего чека в ОФД', sortable: true, filterable: true },
    { id: 'ofd_tariff', label: 'ОФД тариф', sortable: true, filterable: true },
    { id: 'frontol_subscription', label: 'Окончание Frontol', sortable: true, filterable: true },
    { id: 'fr_subscription_status', label: 'Подписка ФР', sortable: true, filterable: true },
    { id: 'cashier_software', label: 'Кассовое ПО', sortable: true, filterable: true },
];
// --- НОВОЕ: Определение обязательных полей ---
const requiredFields = new Set(['client_name', 'inn', 'city', 'rnm', 'zn', 'ofd']);
// --- КОНЕЦ НОВОГО ---
const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'Нет данных';
    try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch {
        return 'Неверная дата';
    }
};
const getFieldValue = (device, fieldId) => {
    if (fieldId === 'id' || fieldId === 'vip' || fieldId === 'client_name'  || fieldId === 'inn' || fieldId === 'city' || fieldId === 'object_name' || fieldId === 'address' || fieldId === 'fr' || fieldId === 'rnm' || fieldId === 'zn' || fieldId === 'ofd' || fieldId === 'ofd_tariff' || fieldId === 'fn_end_date' || fieldId === 'ofd_end_date' || fieldId === 'dreamkas_key_date' || fieldId === 'frontol_subscription' || fieldId === 'fr_subscription_status' || fieldId === 'last_check_date_ofd' || fieldId === 'utm_certificate_expiration' || fieldId === 'dreamkas_accounting_expiration' || fieldId === 'dreamkas_start_expiration' || fieldId === 'cashier_software') {
        return device[fieldId] || '';
    }
    if (fieldId === 'actions') {
        return '';
    }
    if (device.monitoring_status) {
        return device.monitoring_status[fieldId] || '';
    }
    return '';
};
// --- НОВОЕ: Функция debounce ---
function debounce(func, delay) {
    let timeoutId;
    function debounced(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    }
    debounced.cancel = () => {
        clearTimeout(timeoutId);
    };
    return debounced;
}
// --- КОНЕЦ НОВОГО ---
function FiscalDataTable() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterDays, setFilterDays] = useState(30);
    const [filterField, setFilterField] = useState('fn_end_date'); // Это будет единственное поле выбора даты
    const [orderBy, setOrderBy] = useState('');
    const [order, setOrder] = useState('asc');
    const [columnFilters, setColumnFilters] = useState({});
	const [viewMode, setViewMode] = useState('all');
    const [openDialog, setOpenDialog] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
	const [isExporting, setIsExporting] = useState(false);
    // --- НОВОЕ: Состояния для пагинации ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25); // По умолчанию 25 строк на странице
	const [totalCount, setTotalCount] = useState(0); // Для общего количества в пагинации
    // --- КОНЕЦ НОВОГО ---
    // --- ИЗМЕНЕНО: Добавлены watch, setValue и исправлены defaultValues ---
    const { handleSubmit, control, reset, formState: { errors }, watch, setValue } = useForm({
        defaultValues: {
            // Инициализируем потенциально проблемные поля пустыми строками
            client_name: '',
            inn: '',
            city: '',
            rnm: '',
            zn: '',
            ofd: '',
            // Остальные поля могут быть undefined, если не обязательны
            // Или также инициализировать их пустыми строками для единообразия
            vip: '',
            object_name: '',
            address: '',
            fr: '',
            fn_end_date: '',
            ofd_end_date: '',
            dreamkas_key_date: '',
            dreamkas_accounting_expiration: '',
            dreamkas_start_expiration: '',
            utm_certificate_expiration: '',
            last_check_date_ofd: '',
            ofd_tariff: '',
            frontol_subscription: '',
            fr_subscription_status: '',
            cashier_software: '',
        },
    });
    // --- КОНЕЦ ИЗМЕНЕНИЯ ---
    const [uniqueVips, setUniqueVips] = useState([]);
    const [uniqueClientNames, setUniqueClientNames] = useState([]);
    const [uniqueInns, setUniqueInns] = useState([]);
    const [uniqueCities, setUniqueCities] = useState([]);
    const [uniqueOfds, setUniqueOfds] = useState([]);
    // --- НОВОЕ: Отслеживание значения client_name ---
    const watchedClientName = watch('client_name');
    // --- КОНЕЦ НОВОГО ---
    // --- НОВОЕ: Использование useRef для clientNameToInnMap ---
    const clientNameToInnMapRef = useRef({});

    useEffect(() => {
        console.log("Пересоздание clientNameToInnMap в useRef");
        const map = {};
        data.forEach(item => {
            if (item.client_name && item.inn && !map[item.client_name]) {
                 map[item.client_name] = item.inn;
            }
        });
        clientNameToInnMapRef.current = map;
    }, [data]);

    // Debounce для setValue
    const debouncedSetInnValue = useCallback(
        debounce((innValue) => {
            console.log("Установка значения ИНН (debounced + useRef):", innValue);
            setValue('inn', innValue, { shouldValidate: false });
        }, 300), // Задержка 300мс
        [setValue]
    );

    // useEffect для авто-заполнения ИНН с useRef
    useEffect(() => {
        if (watchedClientName) {
            const foundInn = clientNameToInnMapRef.current[watchedClientName];
            if (foundInn) {
                debouncedSetInnValue(foundInn);
            }
        }

        return () => {
            debouncedSetInnValue.cancel();
        };
    }, [watchedClientName, debouncedSetInnValue]);
    // --- КОНЕЦ НОВОГО ---
    const COLUMN_VISIBILITY_STORAGE_KEY = 'fiscalDataTable_columnVisibility';
    const [visibleColumns, setVisibleColumns] = useState(() => {
        try {
            const savedSettings = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
            if (savedSettings) {
                const parsedSettings = JSON.parse(savedSettings);
                if (typeof parsedSettings === 'object' && parsedSettings !== null) {
                     const updatedSettings = {};
                     headCells.forEach(cell => {
                         updatedSettings[cell.id] = parsedSettings[cell.id] !== undefined ? parsedSettings[cell.id] : true;
                     });
                     console.log("Загружены настройки видимости столбцов из localStorage:", updatedSettings);
                     return updatedSettings;
                }
            }
        } catch (e) {
            console.error("Ошибка при загрузке настроек видимости столбцов из localStorage:", e);
            localStorage.removeItem(COLUMN_VISIBILITY_STORAGE_KEY);
        }
        const initialVisibility = {};
        headCells.forEach(cell => {
            initialVisibility[cell.id] = true;
        });
        console.log("Используются настройки видимости столбцов по умолчанию:", initialVisibility);
        return initialVisibility;
    });
    useEffect(() => {
        try {
            localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(visibleColumns));
            console.log("Настройки видимости столбцов сохранены в localStorage:", visibleColumns);
        } catch (e) {
            console.error("Ошибка при сохранении настроек видимости столбцов в localStorage:", e);
        }
    }, [visibleColumns]);
    const [anchorElColumnSettings, setAnchorElColumnSettings] = useState(null);
    const openColumnSettings = Boolean(anchorElColumnSettings);
    const idColumnSettings = openColumnSettings ? 'column-settings-popover' : undefined;
    const [openCredentialsDialog, setOpenCredentialsDialog] = useState(false);
    const [credentialsToDisplay, setCredentialsToDisplay] = useState({ login_ofd: '', password_ofd: '', client_name: '', inn: '' });
	// Новые поля для фильтрации по дате "от" и "до"
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
	const fetchFilteredData = useCallback(async () => {
		setLoading(true);
		setError(null);
		setMessage('');
		try {
			let url = `/fiscal_data_filtered/?skip=${page * rowsPerPage}&limit=${rowsPerPage}`;
			
			// Поиск
			if (searchTerm) {
				url += `&query=${encodeURIComponent(searchTerm)}`;
			}
			
			// Фильтр по дням
			if (viewMode === 'filtered' && filterField && filterDays) {
				url += `&date_field=${filterField}&days_threshold=${filterDays}`;
			}
			
			// Фильтр по датам от/до
			if (dateFrom && dateTo) {
				url += `&from_date=${dateFrom}&to_date=${dateTo}&date_field=${filterField}`;
			}
			
			// Сортировка
			if (orderBy) {
				url += `&sort_by=${orderBy}&sort_order=${order}`;
			}
			
			// Фильтры по колонкам
			Object.keys(columnFilters).forEach(fieldId => {
				if (columnFilters[fieldId] && columnFilters[fieldId].length > 0) {
					columnFilters[fieldId].forEach(value => {
						url += `&${fieldId}=${encodeURIComponent(value)}`;
					});
				}
			});
			
			const response = await axios.get(url);
			setData(response.data.data || []);
			setTotalCount(response.data.total_count || response.data.data.length);
		} catch (err) {
			console.error('Ошибка при загрузке данных:', err);
			setError(err.response?.data?.detail || err.message);
		} finally {
			setLoading(false);
		}
	}, [page, rowsPerPage, searchTerm, viewMode, filterField, filterDays, dateFrom, dateTo, orderBy, order, columnFilters]);

	useEffect(() => {
	  if (searchTerm) {
		setViewMode('search');
	  } else if (viewMode !== 'filtered') {
		setViewMode('all');
	  }
	}, [searchTerm]);

	useEffect(() => {
		fetchFilteredData();
	}, [fetchFilteredData]);

    const fetchUniqueValues = useCallback(async () => {
        try {
            // --- ИЗМЕНЕНО: Загружаем все уникальные значения, игнорируя пагинацию ---
            const response = await axios.get(`/fiscal_data_with_subscription/?limit=9999999`);
            const allData = response.data;
            const vips = [...new Set(allData.map(item => item.vip).filter(Boolean))].sort();
            const clientNames = [...new Set(allData.map(item => item.client_name).filter(Boolean))].sort();
            const inns = [...new Set(allData.map(item => item.inn).filter(Boolean))].sort();
            const cities = [...new Set(allData.map(item => item.city).filter(Boolean))].sort();
            const ofds = [...new Set(allData.map(item => item.ofd).filter(Boolean))].sort();
            setUniqueVips(vips);
            setUniqueClientNames(clientNames);
            setUniqueInns(inns);
            setUniqueCities(cities);
            setUniqueOfds(ofds);
            // --- КОНЕЦ ИЗМЕНЕНИЯ ---
        } catch (err) {
            console.error("Ошибка при загрузке уникальных значений:", err);
        }
    }, []);

    useEffect(() => {
         if (data.length > 0) {
             fetchUniqueValues();
         }
    }, [data, fetchUniqueValues]);

    // --- ИЗМЕНЕНО: handleSearch с отладкой ---
	const handleSearch = useCallback((query) => {
		setSearchTerm(query);
		setPage(0);
	}, []);

	const searchDebounceRef = useRef(null);

	useEffect(() => {
		searchDebounceRef.current = debounce(handleSearch, 400);
	}, [handleSearch]);

	useEffect(() => {
		if (searchTerm) {
			searchDebounceRef.current(searchTerm);
		} else {
			fetchFilteredData();
			if (viewMode !== 'filtered') {
				setViewMode('all');
			}
		}
	}, [searchTerm, fetchFilteredData, viewMode]);
    // --- КОНЕЦ ИЗМЕНЕНИЯ ---

	const handleFilter = () => {
		setViewMode('filtered');
		setPage(0);
		fetchFilteredData();
	};

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        // --- НОВОЕ: Сбросить на первую страницу при сортировке ---
        setPage(0);
        // --- КОНЕЦ НОВОГО ---
    };

    // --- ИЗМЕНЕНО: Обновленная функция обработки фильтрации по колонкам ---
	const handleColumnFilterChange = (fieldId, newFilterValues) => {
		setColumnFilters((prevFilters) => ({
			...prevFilters,
			[fieldId]: newFilterValues,
		}));
		setPage(0);
	};
    // --- КОНЕЦ ИЗМЕНЕНИЯ ---

    // --- ИЗМЕНЕНО: getUniqueValues теперь получает значения из всех данных, а не только текущей страницы ---
    const getUniqueValues = (fieldId) => {
        // Это может быть неэффективно для очень больших наборов данных.
        // В идеале, это должно быть реализовано на сервере.
        // Для демонстрации будем использовать уже загруженные unique* массивы.
        switch (fieldId) {
            case 'vip':
                return uniqueVips;
            case 'client_name':
                return uniqueClientNames;
            case 'inn':
                return uniqueInns;
            case 'city':
                return uniqueCities;
            case 'ofd':
                return uniqueOfds;
            default:
                // fallback: использовать текущие данные (не идеально, но работает для демонстрации)
                const values = filteredData.map(device => getFieldValue(device, fieldId))
                    .filter(value => value !== null && value !== undefined && value !== '');
                return [...new Set(values)].sort();
        }
    };
    // --- КОНЕЦ ИЗМЕНЕНИЯ ---

	const sortedData = useMemo(() => {
		if (!orderBy) return data;
		return [...data].sort((a, b) => {
			let aValue = getFieldValue(a, orderBy);
			let bValue = getFieldValue(b, orderBy);
			if (typeof aValue === 'string') {
				aValue = aValue.toLowerCase();
			}
			if (typeof bValue === 'string') {
				bValue = bValue.toLowerCase();
			}
			if (!isNaN(aValue) && !isNaN(bValue)) {
				aValue = parseFloat(aValue);
				bValue = parseFloat(bValue);
			}
			if (aValue < bValue) {
				return order === 'asc' ? -1 : 1;
			}
			if (aValue > bValue) {
				return order === 'asc' ? 1 : -1;
			}
			return 0;
		});
	}, [data, order, orderBy]);

    // --- ИЗМЕНЕНО: filteredData с отладкой ---
    const filteredData = useMemo(() => {
        console.group("🔄 Recalculating filteredData");
        console.log("sortedData length:", sortedData.length);
        console.log("searchTerm:", `'${searchTerm}'`);
        console.log("viewMode:", viewMode);
        console.log("columnFilters:", columnFilters);

        let result = sortedData; // sortedData основано на `data`

        // --- КРИТИЧЕСКОЕ ИЗМЕНЕНИЕ ---
        // Если мы в режиме поиска ('search'), данные в `data` (и `sortedData`)
        // уже отфильтрованы сервером. Мы НЕ должны фильтровать их снова.
        if (viewMode === 'search') {
            console.log("✅ Режим 'search'. Используем данные, уже отфильтрованные сервером. Пропускаем фильтрацию на фронтенде по searchTerm.");
            // `result` уже содержит отфильтрованные сервером данные
            // Ничего не делаем с `result` относительно `searchTerm`
        } else if (searchTerm) {
            // Если НЕ в режиме 'search', но `searchTerm` есть
            // (например, пользователь ввел текст, но еще не нажал Enter/кнопку поиска)
            console.log("🔎 Режим НЕ 'search', но есть searchTerm. Применяем фильтр на фронтенде.");
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            result = result.filter(device =>
                Object.values(device).some(value =>
                    String(value).toLowerCase().includes(lowerCaseSearchTerm)
                ) || (device.monitoring_status && Object.values(device.monitoring_status).some(value =>
                    String(value).toLowerCase().includes(lowerCaseSearchTerm)
                ))
            );
        } else {
            console.log("📄 Режим 'all' или 'filtered' без активного поиска на фронтенде.");
        }
        // --- КОНЕЦ КРИТИЧЕСКОГО ИЗМЕНЕНИЯ ---

        // Фильтрация по колонкам применяется всегда, независимо от режима
        console.log("🔍 Применение фильтрации по колонкам...");
        Object.keys(columnFilters).forEach(fieldId => {
            const filterValue = columnFilters[fieldId];
            if (filterValue && filterValue.length > 0) {
                console.log(`  - Фильтр по колонке '${fieldId}':`, filterValue);
                result = result.filter(device => {
                    const fieldValue = getFieldValue(device, fieldId);
                    const includes = filterValue.includes(String(fieldValue));
                    console.log(`    Значение поля: '${fieldValue}', Совпадение: ${includes}`);
                    return includes;
                });
            }
        });

        console.log("🔢 Удаление дубликатов...");
        const seenIds = new Set();
        const initialLength = result.length;
        result = result.filter(item => {
            if (seenIds.has(item.id)) {
                console.warn(`Дубликат записи с ID ${item.id} удален.`, item);
                return false;
            }
            seenIds.add(item.id);
            return true;
        });
        console.log(`  Удалено дубликатов: ${initialLength - result.length}`);

        console.log("🏁 Final filteredData length:", result.length);
        console.groupEnd();
        return result;
    }, [sortedData, searchTerm, columnFilters, viewMode]); // Добавлена зависимость от viewMode
    // --- КОНЕЦ ИЗМЕНЕНИЯ ---

    const getDisplayValue = (device, fieldId) => {
        if (fieldId === 'id' || fieldId === 'vip' || fieldId === 'client_name' || fieldId === 'inn' || fieldId === 'city' || fieldId === 'object_name' || fieldId === 'address' || fieldId === 'fr' || fieldId === 'rnm' || fieldId === 'zn' || fieldId === 'ofd' || fieldId === 'ofd_tariff' || fieldId === 'utm_certificate_expiration' || fieldId === 'dreamkas_accounting_expiration' || fieldId === 'dreamkas_start_expiration' || fieldId === 'cashier_software') {
            return device[fieldId] || 'Н/Д';
        }
        if (fieldId === 'actions') {
            return 'Н/Д';
        }
        if (fieldId === 'po_date') {
            const dateValue = device.monitoring_status?.[fieldId];
            return dateValue ? new Date(dateValue).toLocaleDateString('ru-RU') : 'Н/Д';
        }
        if (fieldId === 'last_checked_at') {
            return formatDateTime(device.monitoring_status?.[fieldId]);
        }
        if (fieldId === 'fn_end_date' || fieldId === 'ofd_end_date' || fieldId === 'dreamkas_key_date' || fieldId === 'frontol_subscription' || fieldId === 'last_check_date_ofd' || fieldId === 'dreamkas_accounting_expiration' || fieldId === 'dreamkas_start_expiration' || fieldId === 'utm_certificate_expiration') {
            const dateValue = device[fieldId];
            return dateValue ? new Date(dateValue).toLocaleDateString('ru-RU') : 'Н/Д';
        }
        return device.monitoring_status?.[fieldId] || 'Н/Д';
    };
    const handleAddClick = useCallback(() => {
        setCurrentRecord(null);
        // Сброс формы с пустыми строками для избежания ошибок контролируемости
        reset({
            client_name: '',
            inn: '',
            city: '',
            rnm: '',
            zn: '',
            ofd: '',
            vip: '',
            object_name: '',
            address: '',
            fr: '',
            fn_end_date: '',
            ofd_end_date: '',
            dreamkas_key_date: '',
            dreamkas_accounting_expiration: '',
            dreamkas_start_expiration: '',
            utm_certificate_expiration: '',
            last_check_date_ofd: '',
            ofd_tariff: '',
            frontol_subscription: '',
            fr_subscription_status: '',
            cashier_software: '',
        });
        setOpenDialog(true);
    }, [reset]);
    const handleEditClick = useCallback((record) => {
        setCurrentRecord(record);
        const initialData = {};
        for (const key in record) {
            if (key.includes('date') || key.includes('subscription') || key.includes('expiration') || key === 'dreamkas_accounting_expiration' || key === 'dreamkas_start_expiration') {
                initialData[key] = record[key] ? new Date(record[key]).toISOString().split('T')[0] : '';
            } else {
                // Убедиться, что строки не undefined
                initialData[key] = record[key] ?? '';
            }
        }
        reset(initialData);
        setOpenDialog(true);
    }, [reset]);
    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setCurrentRecord(null);
        reset({
            client_name: '',
            inn: '',
            city: '',
            rnm: '',
            zn: '',
            ofd: '',
            vip: '',
            object_name: '',
            address: '',
            fr: '',
            fn_end_date: '',
            ofd_end_date: '',
            dreamkas_key_date: '',
            dreamkas_accounting_expiration: '',
            dreamkas_start_expiration: '',
            utm_certificate_expiration: '',
            last_check_date_ofd: '',
            ofd_tariff: '',
            frontol_subscription: '',
            fr_subscription_status: '',
            cashier_software: '',
        });
    }, [reset]);
    const handleCloseCredentialsDialog = useCallback(() => {
        setOpenCredentialsDialog(false);
        setCredentialsToDisplay({ login_ofd: '', password_ofd: '', client_name: '', inn: '' });
    }, []);
    const onSubmit = useCallback(async (formData) => {
        setLoading(true);
        setError(null);
        setMessage('');
        try {
            const dataToSend = { ...formData };
            for (const key in dataToSend) {
                if (key.includes('date') || key.includes('subscription') || key.includes('expiration') || key === 'dreamkas_accounting_expiration' || key === 'dreamkas_start_expiration') {
                    if (dataToSend[key]) {
                        dataToSend[key] = new Date(dataToSend[key]).toISOString().split('T')[0];
                    } else {
                        dataToSend[key] = null;
                    }
                }
            }
            if (currentRecord) {
                await axios.put(`/fiscal_data/${currentRecord.id}`, dataToSend);
                console.log(`Запись с ID ${currentRecord.id} обновлена.`);
                setMessage('Запись успешно обновлена.');
            } else {
                await axios.post(`/fiscal_data/`, dataToSend);
                console.log('Новая запись создана.');
                setMessage('Новая запись успешно создана.');
            }
            await fetchFilteredData(); // Перезагрузить данные с учетом пагинации
            handleCloseDialog();
        } catch (err) {
            console.error('Ошибка при сохранении записи:', err.response ? err.response.data : err.message);
            setError(err);
            alert(`Ошибка при сохранении: ${err.response ? JSON.stringify(err.response.data) : err.message}`);
        } finally {
            setLoading(false);
        }
    }, [currentRecord, fetchFilteredData, handleCloseDialog]);
    const handleDeleteRecord = useCallback(async (id) => {
        if (window.confirm(`Вы уверены, что хотите удалить запись с ID ${id}?`)) {
            setLoading(true);
            setError(null);
            setMessage('');
            try {
                await axios.delete(`/fiscal_data/${id}`);
                console.log(`Запись с ID ${id} удалена.`);
                setMessage('Запись успешно удалена.');
                await fetchFilteredData(); // Перезагрузить данные с учетом пагинации
            } catch (err) {
                console.error('Ошибка при удалении записи:', err.response ? err.response.data : err.message);
                setError(err);
                alert(`Ошибка при удалении: ${err.response ? JSON.stringify(err.response.data) : err.message}`);
            } finally {
                setLoading(false);
            }
        }
    }, [fetchFilteredData]);
	const handleExportToExcel = useCallback(async () => {
	  setIsExporting(true);
	  setError(null);
	  setMessage('Экспорт запущен...');

	  try {
		const params = new URLSearchParams({
		  skip: '0',
		  limit: '999999',
		  search_query: searchTerm || '',
		  filter_field: filterField || '',
		  days_threshold: filterDays?.toString() || '',
		  order_by: orderBy || '',
		  order: order || 'asc',
		});

		if (Object.keys(columnFilters).length > 0) {
		  params.set('column_filters', JSON.stringify(columnFilters));
		}

		if (dateFrom) params.set('from_date', dateFrom);
		if (dateTo) params.set('to_date', dateTo);

		console.log('Экспорт → /fiscal_data/export_excel/?' + params.toString());

		const response = await axios.get(
		  `https://sks-ms.duckdns.org/fiscal_data/export_excel/?${params.toString()}`,
		  {
			responseType: 'blob',
			timeout: 300000, // 5 минут на большие выгрузки
		  }
		);

		// Скачиваем файл
		const url = window.URL.createObjectURL(new Blob([response.data]));
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `fiscal_data_${new Date().toISOString().slice(0,10)}.xlsx`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);

		setMessage('Excel успешно скачан!');
	  } catch (err) {
		console.error('Ошибка экспорта:', err);
		if (err.response?.status === 500) {
		  setMessage('Ошибка на сервере. Возможно, слишком много данных или сбой при формировании файла.');
		} else {
		  setMessage('Не удалось скачать файл. Проверьте интернет или фильтры.');
		}
	  } finally {
		setIsExporting(false);
	  }
	}, [
	  searchTerm,
	  filterField,
	  filterDays,
	  dateFrom,
	  dateTo,
	  orderBy,
	  order,
	  columnFilters,
	]);
	const handleUpdateSingle = useCallback(async (id) => {
		setLoading(true);
		setError(null);
		setMessage('');
		try {
			const response = await axios.get(`/update_single_device/${id}`);
			const updatedRecordFromServer = response.data;
			setData(prevData =>
				prevData.map(item =>
					item.id === id ? { ...item, ...updatedRecordFromServer } : item
				)
			);
			setMessage(`Запись с ID ${id} успешно обновлена (Taxcom + Dreamkas).`);
		} catch (err) {
			console.error(`Ошибка при обновлении записи с ID ${id}:`, err);
			if (err.response) {
				setError(new Error(`Ошибка ${err.response.status}: ${err.response.data?.detail || err.message}`));
				setMessage(`Ошибка при обновлении записи с ID ${id}: ${err.response.data?.detail || err.message}`);
			} else if (err.request) {
				setError(new Error('Сервер не отвечает.'));
				setMessage(`Ошибка при обновлении записи с ID ${id}: Сервер не отвечает.`);
			} else {
				setError(new Error(`Ошибка запроса: ${err.message}`));
				setMessage(`Ошибка при обновлении записи с ID ${id}: ${err.message}`);
			}
        } finally {
            setLoading(false);
        }
    }, []);
    const handleInitiateTaxcomSessionAndOpenLK = useCallback(async (recordId, inn) => {
        setLoading(true);
        setError(null);
        setMessage('');
        try {
            if (!inn) {
                 throw new Error("У записи отсутствует ИНН. Невозможно получить учетные данные ОФД.");
            }
            const response = await axios.post(`/initiate_taxcom_session_by_inn/${recordId}`);
            const { message: backendMessage, client_name } = response.data;
            window.open('http://lk-ofd.taxcom.ru', '_blank');
             const credResponse = await axios.get(`/fiscal_data/${recordId}/ofd_credential`);
             const credential = credResponse.data;
            setCredentialsToDisplay({
                login_ofd: credential.login_ofd,
                password_ofd: credential.password_ofd,
                client_name: credential.client_name || client_name || '',
                inn: inn || ''
            });
            setOpenCredentialsDialog(true);
            setMessage(backendMessage || `Сессия Taxcom для ИНН '${inn}' инициирована. Открыта новая вкладка. Учетные данные загружены.`);
        } catch (err) {
            console.error(`Ошибка при инициации сессии Taxcom для записи ID ${recordId}:`, err);
            let errorMsg = 'Неизвестная ошибка';
            if (err.response) {
                if (err.response.status === 404) {
                    errorMsg = `Запись или учетные данные ОФД не найдены.`;
                } else if (err.response.status === 403) {
                    errorMsg = `Доступ запрещен.`;
                } else if (err.response.status === 400) {
                    errorMsg = err.response.data?.detail || 'Ошибка авторизации в Taxcom.';
                } else {
                    errorMsg = `Ошибка ${err.response.status}: ${err.response.data?.detail || err.message}`;
                }
                setError(new Error(errorMsg));
            } else if (err.request) {
                errorMsg = 'Сервер не отвечает.';
                setError(new Error(errorMsg));
            } else {
                errorMsg = `Ошибка запроса: ${err.message}`;
                setError(new Error(errorMsg));
            }
            setMessage(`Ошибка: ${errors}`);
        } finally {
            setLoading(false);
        }
    }, []);
	// --- ИСПРАВЛЕНО (еще раз): Логика для перетаскивания таблицы (Drag-to-Scroll с Ctrl) ---
	const tableContainerRef = useRef(null);
	useEffect(() => {
		const container = tableContainerRef.current;
		if (!container) {
			console.log("Контейнер таблицы не найден в useEffect");
			return;
		}
		console.log("Контейнер таблицы найден:", container);
		console.log("Контейнер scrollWidth:", container.scrollWidth);
		console.log("Контейнер clientWidth:", container.clientWidth);
		console.log("Контейнер scrollHeight:", container.scrollHeight);
		console.log("Контейнер clientHeight:", container.clientHeight);
		// Устанавливаем начальные стили
		container.style.cursor = 'grab';
		container.style.overflowX = 'scroll'; // Принудительно показываем скроллбар
		container.style.overflowY = 'hidden'; // Отключаем вертикальный скролл, как было
		let isDragging = false;
		let startPos = { x: 0, y: 0 };
		let scrollPos = { left: 0, top: 0 };
		const handleMouseDown = (e) => {
			console.log("MouseDown event triggered", e);
			if (!e.ctrlKey) {
				console.log("Ctrl не зажат, игнорируем событие");
				return;
			}
			// ВАЖНО: Предотвращаем стандартное поведение сразу
			e.preventDefault();
			console.log("Ctrl зажат, начинаем перетаскивание");
			isDragging = true;
			startPos = { x: e.clientX, y: e.clientY };
			scrollPos = { left: container.scrollLeft, top: container.scrollTop };
			console.log("Начальная позиция мыши:", startPos);
			console.log("Начальная позиция прокрутки:", scrollPos);
			// Устанавливаем стиль курсора при начале перетаскивания
			container.style.cursor = 'grabbing';
			container.style.userSelect = 'none';
			// Добавляем глобальные слушатели
			// Не используем { passive: false }
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			// document.addEventListener('mouseleave', handleMouseUp); // Попробуем без него сначала
		};
		const handleMouseMove = (e) => {
			if (!isDragging) return;
			// ВАЖНО: Предотвращаем стандартное поведение
			e.preventDefault();
			// Вычисляем смещение
			const dx = e.clientX - startPos.x;
			// const dy = e.clientY - startPos.y; // Не используем dy для вертикальной прокрутки
			console.log(`MouseMove: dx=${dx}`); // , dy=${dy}
			// Вычисляем новую позицию прокрутки
			// Используем scrollPos (начальную позицию прокрутки) и dx (смещение мыши)
			const newScrollLeft = scrollPos.left - dx;
			// const newScrollTop = scrollPos.top - dy;
			console.log(`Попытка прокрутки: scrollLeft=${newScrollLeft}`); // , scrollTop=${newScrollTop}
			// Устанавливаем новую позицию прокрутки
			container.scrollLeft = newScrollLeft;
			// container.scrollTop = newScrollTop; // Не трогаем вертикальную прокрутку
			// Сразу проверяем, установилось ли значение
			console.log(`Фактическая прокрутка после установки: scrollLeft=${container.scrollLeft}`); // , scrollTop=${container.scrollTop}
		};
		const handleMouseUp = (e) => {
			console.log("MouseUp event triggered", e);
			if (!isDragging) return;
			isDragging = false;
			// Восстанавливаем стили
			container.style.cursor = 'grab';
			container.style.userSelect = '';
			// Удаляем глобальные слушатели
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			// document.removeEventListener('mouseleave', handleMouseUp);
			console.log("Глобальные слушатели удалены");
		};
		// Добавляем слушатель mousedown
		console.log("Добавляем mousedown listener на контейнер");
		container.addEventListener('mousedown', handleMouseDown);
		// Функция очистки
		return () => {
			console.log("Очистка useEffect для перетаскивания");
			container.removeEventListener('mousedown', handleMouseDown);
			// На случай, если компонент размонтируется во время перетаскивания
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			// document.removeEventListener('mouseleave', handleMouseUp);
		};
	}, []); // Пустой массив зависимостей
	// --- КОНЕЦ ИСПРАВЛЕНИЯ ---
    // --- НОВОЕ: Обработка фильтрации по дате "от" и "до" ---
	const handleDateFilter = () => {
		setViewMode('filtered');
		setPage(0);
		fetchFilteredData();
	};
    // --- КОНЕЦ НОВОГО ---
    // --- НОВОЕ: Обработчики событий пагинации ---
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Сбросить на первую страницу при изменении количества строк
    };
    // --- КОНЕЦ НОВОГО ---
    // --- НОВОЕ: Логгирование состояния data и filteredData ---
    useEffect(() => {
        console.log("🧮(data) Количество записей в состоянии 'data':", data.length);
        if (data.length > 0) {
            console.log("🧾(data) Первые 3 записи в 'data':", data.slice(0, 3));
        }
    }, [data]); // Зависимость от data

    useEffect(() => {
        console.log("🧮(filteredData) Количество записей для отображения в таблице:", filteredData.length);
        if (filteredData.length > 0) {
            console.log("🧾(filteredData) Первые 3 записи для отображения:", filteredData.slice(0, 3));
        }
    }, [filteredData]); // Зависимость от filteredData
    // --- КОНЕЦ НОВОГО ---
    return (
        <Box sx={{ p: 2, fontFamily: 'Roboto, sans-serif', fontWeight: 'bold' }}>
            {message && !error && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
            {/* --- ИЗМЕНЕНО: Добавлены обработчики поиска --- */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    label="Поиск по фискальным данным"
                    variant="outlined"
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    // --- НОВОЕ: Обработчик нажатия клавиши ---
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault(); // Предотвратить отправку формы, если TextField внутри формы
                            console.log("Нажата клавиша Enter в поле поиска. Запуск поиска:", searchTerm);
                            if (searchTerm.trim()) { // Запускать только если есть непустой запрос
                                handleSearch(searchTerm.trim());
                            } else {
                                console.log("Поисковый запрос пустой. Сброс фильтров.");
                                fetchFilteredData(); // Или сбросить фильтры, если поле очищено
                                setPage(0);
                            }
                        }
                    }}
                    // --- КОНЕЦ НОВОГО ---
                    margin="normal"
                    sx={{ flexGrow: 1, minWidth: '250px' }}
                />
                {/* --- НОВОЕ: Кнопка "Поиск" --- */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        console.log("Нажата кнопка 'Поиск'. Запуск поиска:", searchTerm);
                        if (searchTerm.trim()) {
                            handleSearch(searchTerm.trim());
                        } else {
                            console.log("Поисковый запрос пустой. Сброс фильтров.");
                            fetchFilteredData();
                            setPage(0);
                        }
                    }}
                    sx={{ height: '56px' }}
                    disabled={!searchTerm.trim()} // Отключить кнопку, если запрос пустой
                >
                    Поиск
                </Button>
                {/* --- КОНЕЦ НОВОГО --- */}
				<Button 
				  variant="outlined" 
				  color="error"
				  onClick={() => {
					// 1. Сбрасываем ВСЁ, что может быть фильтром
					setSearchTerm('');
					setColumnFilters({});
					setFilterDays(30);
					setFilterField('fn_end_date');
					setDateFrom('');
					setDateTo('');
					setOrderBy('');
					setOrder('asc');
					setViewMode('all');

					// 2. Сбрасываем пагинацию
					setPage(0);
					setRowsPerPage(25);

					// 3. Перезагружаем данные с сервера (как при F5)
					fetchFilteredData();
				  }} 
				  sx={{ height: '56px' }}
				>
				  Сбросить поиск и фильтры
				</Button>
                <FormControl variant="outlined" sx={{ minWidth: 120, height: '56px' }}>
                    <InputLabel>Фильтр</InputLabel>
                    <Select
                        value={filterField}
                        onChange={(e) => setFilterField(e.target.value)}
                        label="Поле даты"
                        sx={{ height: '56px' }}
                    >
                        <MenuItem value="all">Все</MenuItem>
                        <MenuItem value="fn_end_date">Срок окончания ФН</MenuItem>
                        <MenuItem value="ofd_end_date">Срок окончания ОФД</MenuItem>
                        <MenuItem value="frontol_subscription">Окончание Frontol</MenuItem>
                        <MenuItem value="dreamkas_key_date">Дримкас ключ</MenuItem>
                        <MenuItem value="dreamkas_accounting_expiration">Дримкас учет</MenuItem>
                        <MenuItem value="dreamkas_start_expiration">Дримкас старт</MenuItem>
                        <MenuItem value="utm_certificate_expiration">Сертификат УТМ</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label="Дней до окончания"
                    variant="outlined"
                    type="number"
                    value={filterDays}
                    onChange={(e) => setFilterDays(e.target.value)}
                    sx={{ width: 100, height: '56px' }}
                    InputProps={{ sx: { height: '56px' } }}
                    InputLabelProps={{ sx: { height: '56px', lineHeight: '56px' } }}
                />
                <Button variant="contained" color="primary" onClick={handleFilter} sx={{ height: '56px' }}>
                    Фильтр по кол-ву дней
                </Button>
                {/* Новые поля для фильтрации по дате "от" и "до" */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        label="От"
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{ width: 150, height: '56px' }}
                    />
                    <TextField
                        label="До"
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{ width: 150, height: '56px' }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDateFilter}
                        sx={{ height: '56px' }}
                    >
                        Фильтр по дате
                    </Button>
                </Box>
                <IconButton
                    aria-label="Настройки столбцов"
                    onClick={(event) => setAnchorElColumnSettings(event.currentTarget)}
                    sx={{ height: '56px' }}
                >
                    <SettingsIcon />
                </IconButton>
                <Popover
                    id={idColumnSettings}
                    open={openColumnSettings}
                    anchorEl={anchorElColumnSettings}
                    onClose={() => setAnchorElColumnSettings(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2, maxHeight: 400, overflowY: 'auto' }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Отображаемые столбцы</Typography>
                        {headCells.map((cell) => (
                            <Box key={cell.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Checkbox
                                    checked={visibleColumns[cell.id] !== false}
                                    onChange={(e) => {
                                        setVisibleColumns(prev => ({
                                            ...prev,
                                            [cell.id]: e.target.checked
                                        }));
                                    }}
                                />
                                <ListItemText primary={cell.label} />
                            </Box>
                        ))}
                    </Box>
                </Popover>
				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
					<Button
						variant="contained"
						color="success"
						onClick={handleExportToExcel}
						startIcon={isExporting ? <CircularProgress size={24} /> : <DownloadIcon />}
						sx={{ height: '56px' }}
						disabled={isExporting || loading}
					>
						{isExporting ? 'Экспорт...' : 'Экспорт в Excel'}
					</Button>
					<Button
						variant="contained"
						color="secondary"
						startIcon={<AddIcon />}
						onClick={handleAddClick}
						sx={{ height: '56px' }}
					>
						Добавить запись
					</Button>
				</Box>
            </Box>
            {/* --- КОНЕЦ ИЗМЕНЕНИЯ --- */}
            <Typography variant="body2" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
                * Удерживайте <kbd>Ctrl+ЛКМ</kbd> для перетаскивания таблицы.
            </Typography>
            <Box
                sx={{
                    mt: 2,
                    maxHeight: 'calc(100vh - 300px)', // Увеличена высота для размещения пагинации
					width: '100%',
                    //maxWidth: '2200px',
                }}
            >
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    sx={{
                        display: 'block',
                        overflowX: 'auto',
                        cursor: 'grab',
                        scrollBehavior: 'smooth',
                        minHeight: '100px',
                        //minWidth: 2800,
						width: '100%',
                    }}
                >
                    <Table stickyHeader aria-label="fiscal data table" sx={{ minWidth: 1000 }}>
                        <TableHead>
                            <TableRow>
                                {headCells
                                    .filter((cell) => visibleColumns[cell.id] !== false)
                                    .map((headCell) => (
                                    <TableCell
                                        key={headCell.id}
                                        sx={{
                                            color: 'text.primary',
                                            backgroundColor: 'background.paper',
                                            minWidth: headCell.id === 'client_name' ? 180 : 'auto',
                                            position: 'relative',
                                            fontWeight: 'bold',
                                            height: '56px',
                                            verticalAlign: 'middle',
                                        }}
                                        sortDirection={orderBy === headCell.id ? order : false}
                                    >
                                        {headCell.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(headCell.id)}
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                {headCell.label}
                                            </TableSortLabel>
                                        ) : (
                                            <Box sx={{ fontWeight: 'bold' }}>{headCell.label}</Box>
                                        )}
                                        {headCell.filterable && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                                <FormControl fullWidth size="small" sx={{ mt: 0.5, width: '100%' }}>
                                                    <Select
                                                        multiple
                                                        displayEmpty
                                                        value={columnFilters[headCell.id] || []}
                                                        onChange={(e) => handleColumnFilterChange(headCell.id, e.target.value)}
                                                        renderValue={(selected) => {
                                                            if (selected.length === 0) {
                                                                return <em>Все</em>;
                                                            }
                                                            return selected.join(', ');
                                                        }}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'secondary.main' },
                                                            '.MuiSvgIcon-root': { color: 'secondary.main' },
                                                        }}
                                                    >
                                                        <MenuItem disabled value="">
                                                            <em>Выберите значения</em>
                                                        </MenuItem>
                                                        {getUniqueValues(headCell.id).map((value) => (
                                                            <MenuItem key={value} value={value}>
                                                                <Checkbox checked={(columnFilters[headCell.id] || []).indexOf(value) > -1} />
                                                                <ListItemText primary={value} />
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={headCells.length} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : filteredData.length === 0 && !message.includes('Ничего не найдено') ? (
                                <TableRow>
                                    <TableCell colSpan={headCells.length} align="center">
                                        Нет данных для отображения.
                                    </TableCell>
                                </TableRow>
                            ) : filteredData.length === 0 && message.includes('Ничего не найдено') ? (
                                 <TableRow>
                                    <TableCell colSpan={headCells.length} align="center">
                                    </TableCell>
                                </TableRow>
                            ) : (
                                // --- НОВОЕ: Логгирование внутри рендера ---
                                <>
                                    {console.log("🖌️ Рендер строк таблицы. Количество строк:", filteredData.length)}
                                    {data.map((row) => (
                                        <FiscalDataRow
                                            key={row.id}
                                            row={row}
                                            headCells={headCells.filter(cell => visibleColumns[cell.id] !== false)}
                                            onEdit={handleEditClick}
                                            onDelete={handleDeleteRecord}
                                            getDisplayValue={getDisplayValue}
                                            onUpdateSingle={handleUpdateSingle}
                                            onInitiateTaxcomSessionAndOpenLK={handleInitiateTaxcomSessionAndOpenLK}
                                        />
                                    ))}
                                </>
                                // --- КОНЕЦ НОВОГО ---
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* --- НОВОЕ: Компонент пагинации --- */}
				<TablePagination
					rowsPerPageOptions={[25, 50, 100]}
					component="div"
					count={totalCount}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={(event, newPage) => setPage(newPage)}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
				/>
                {/* --- КОНЕЦ НОВОГО --- */}
            </Box>
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{currentRecord ? 'Редактировать запись' : 'Добавить новую запись'}</DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent>
                        {headCells.filter(cell => cell.id !== 'id' && cell.id !== 'actions' && cell.id !== 'last_updated' && cell.id !== 'fr_subscription_status').map(cell => (
                            <Controller
                                key={cell.id}
                                name={cell.id}
                                control={control}
                                // --- ИЗМЕНЕНО: Обновлены правила валидации ---
                                rules={{ required: requiredFields.has(cell.id) }}
                                // --- КОНЕЦ ИЗМЕНЕНИЯ ---
                                render={({ field }) => {
                                    const isAutocompleteField = ['vip', 'client_name', 'inn', 'city', 'ofd'].includes(cell.id);
                                    // --- НОВОЕ: Определение, является ли поле обязательным ---
                                    const isRequired = requiredFields.has(cell.id);
                                    // --- КОНЕЦ НОВОГО ---
                                    if (isAutocompleteField) {
                                        let options = [];
                                        // --- ИЗМЕНЕНО: Обновлено отображение метки ---
                                        let label = isRequired ? <><strong>{cell.label}</strong> <span style={{ fontWeight: 'normal', color: 'red' }}>(обязательное поле)</span></> : cell.label;
                                        // --- КОНЕЦ ИЗМЕНЕНИЯ ---
                                        switch (cell.id) {
                                            case 'vip':
                                                options = uniqueVips;
                                                break;
                                            case 'client_name':
                                                options = uniqueClientNames;
                                                break;
                                            case 'inn':
                                                options = uniqueInns;
                                                break;
                                            case 'city':
                                                options = uniqueCities;
                                                break;
                                            case 'ofd':
                                                options = uniqueOfds;
                                                break;
                                            default:
                                                options = [];
                                        }
                                        return (
                                            <Autocomplete
                                                {...field}
                                                options={options}
                                                value={field.value || ''}
                                                onInputChange={(event, newInputValue) => {
                                                    field.onChange(newInputValue);
                                                }}
                                                onChange={(event, newValue) => {
                                                    field.onChange(newValue || '');
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={label}
                                                        fullWidth
                                                        margin="normal"
                                                        // --- ИЗМЕНЕНО: Обновлены атрибуты required и disabled ---
                                                        required={isRequired}
                                                        disabled={cell.id === 'rnm' && currentRecord}
                                                        // --- КОНЕЦ ИЗМЕНЕНИЯ ---
                                                        error={!!errors[cell.id]}
                                                        helperText={errors[cell.id] ? (errors[cell.id].type === 'required' ? 'Это поле обязательно' : errors[cell.id].message) : ''}
                                                    />
                                                )}
                                                freeSolo
                                                selectOnFocus
                                                clearOnBlur={false}
                                                handleHomeEndKeys
                                            />
                                        );
                                    } else {
                                        return (
                                            // --- ИЗМЕНЕНО: Обновлено отображение метки ---
                                            <TextField
                                                {...field}
                                                label={isRequired ? <><strong>{cell.label}</strong> <span style={{ fontWeight: 'normal', color: 'red' }}>(обязательное поле)</span></> : cell.label}
                                                // --- КОНЕЦ ИЗМЕНЕНИЯ ---
                                                type={
                                                    cell.id.includes('date') ||
                                                    cell.id.includes('subscription') ||
                                                    cell.id.includes('expiration') ||
                                                    cell.id === 'dreamkas_accounting_expiration' ||
                                                    cell.id === 'dreamkas_start_expiration' ||
                                                    cell.id === 'utm_certificate_expiration'
                                                        ? 'date'
                                                        : 'text'
                                                }
                                                fullWidth
                                                margin="normal"
                                                // --- ИЗМЕНЕНО: Обновлены атрибуты required и disabled ---
                                                required={isRequired}
                                                disabled={cell.id === 'rnm' && currentRecord}
                                                // --- КОНЕЦ ИЗМЕНЕНИЯ ---
                                                error={!!errors[cell.id]}
                                                helperText={
                                                    errors[cell.id]
                                                        ? errors[cell.id].type === 'required'
                                                            ? 'Это поле обязательно'
                                                            : errors[cell.id].message
                                                        : ''
                                                }
                                                InputLabelProps={
                                                    cell.id.includes('date') ||
                                                    cell.id.includes('subscription') ||
                                                    cell.id.includes('expiration') ||
                                                    cell.id === 'dreamkas_accounting_expiration' ||
                                                    cell.id === 'dreamkas_start_expiration' ||
                                                    cell.id === 'utm_certificate_expiration'
                                                        ? { shrink: true }
                                                        : {}
                                                }
                                            />
                                        );
                                    }
                                }}
                            />
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} type="button">Отмена</Button>
                        <Button type="submit" color="primary" variant="contained">
                            Сохранить
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
            <Dialog
                open={openCredentialsDialog}
                onClose={handleCloseCredentialsDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Учетные данные для входа в ЛК Такском</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Открыта новая вкладка с <Link href="http://lk-ofd.taxcom.ru" target="_blank" rel="noopener">Личным кабинетом Такском</Link>.
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        Используйте следующие учетные данные для входа:
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Логин:</Typography>
                                <Box display="flex" alignItems="center">
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={credentialsToDisplay.login_ofd}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        sx={{ bgcolor: 'background.paper' }}
                                    />
                                    <IconButton
                                        onClick={() => navigator.clipboard.writeText(credentialsToDisplay.login_ofd).then(() => {
                                            console.log('Логин скопирован');
                                        }).catch(err => {
                                            console.error('Ошибка копирования логина: ', err);
                                        })}
                                        aria-label="копировать логин"
                                        size="small"
                                        sx={{ ml: 1 }}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Пароль:</Typography>
                                <Box display="flex" alignItems="center">
                                     <TextField
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        type="text"
                                        value={credentialsToDisplay.password_ofd}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        sx={{ bgcolor: 'background.paper' }}
                                    />
                                    <IconButton
                                        onClick={() => navigator.clipboard.writeText(credentialsToDisplay.password_ofd).then(() => {
                                            console.log('Пароль скопирован');
                                        }).catch(err => {
                                            console.error('Ошибка копирования пароля: ', err);
                                        })}
                                        aria-label="копировать пароль"
                                        size="small"
                                        sx={{ ml: 1 }}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        </Grid>
                        {(credentialsToDisplay.client_name || credentialsToDisplay.inn) && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">Информация:</Typography>
                                <Typography variant="body2">
                                    {credentialsToDisplay.client_name && `Клиент: ${credentialsToDisplay.client_name}`}
                                    {credentialsToDisplay.client_name && credentialsToDisplay.inn && <br />}
                                    {credentialsToDisplay.inn && `ИНН: ${credentialsToDisplay.inn}`}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCredentialsDialog} color="primary" variant="contained">
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>
            <div style={{ height: '50px' }}></div>
        </Box>
    );
}
export default FiscalDataTable;
